import {
  Box,
  forwardRef,
  useColorModeValue,
  useDisclosure,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { FileUploader } from '@remirror/core';
import { RemirrorJSON } from '@remirror/core-types';
import { FileAttributes, FileExtension } from '@remirror/extension-file';
import { ImageAttributes, ImageExtension } from '@remirror/extension-image';
import {
  EditorComponent,
  ReactComponentExtension,
  Remirror,
  useRemirror,
} from '@remirror/react';
import { AllStyledComponent } from '@remirror/styles/emotion';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  BoldExtension,
  BulletListExtension,
  DropCursorExtension,
  EventsExtension,
  HeadingExtension,
  HistoryExtension,
  HorizontalRuleExtension,
  ItalicExtension,
  MarkdownExtension,
  MentionAtomExtension,
  NodeFormattingExtension,
  OrderedListExtension,
  StrikeExtension,
  TextHighlightExtension,
  TrailingNodeExtension,
  UnderlineExtension,
} from 'remirror/extensions';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import AddBlocksToolbar from './AddBlocksToolbar';
import './editor.css';
import { FloatingToolbar } from './FloatingToolbar';
import { MouseTrackerExtension } from './MouseTrackerExtension';
import TemplateExtension from './TemplateExtension';
import { TodoExtension } from './TodoExtension';
import {
  useUploadFileHandler,
  useUploadImageHandler,
} from './UploadFileHandler';
import UserSuggester from './UserSuggester';
import { CustomLinkExtension } from './CustomLinkExtension';
import { ThemeProvider } from './ThemeProvider';
import { LinkCardExtension } from './LinkCardExtension';
import DetectLinkCardDialog from './DetectLinkCardDialog';
import { ErrorBoundary } from '@sentry/react';
import { LinkToolModal } from './FloatingToolbar/LinkTool';
import { changeFilesUrlToCurrentLocation } from './utils';
import * as Sentry from '@sentry/react';
import { InvalidContentHandler } from 'remirror';
import { CollapsingToolbar } from './FloatingToolbar/CollapsingToolbar';

type ImageUploaderFunction = (
  file: File,
  setProgress: (progress: number) => void,
) => Promise<ImageAttributes>;

function getExtensions(
  fileUploader: () => FileUploader<FileAttributes>,
  imageUploader: ImageUploaderFunction | undefined,
  isDisabled: boolean,
  isMobile: boolean,
  hasBorder: boolean,
) {
  return () => [
    new BoldExtension(),
    new ItalicExtension(),
    new UnderlineExtension(),
    new StrikeExtension(),
    new CustomLinkExtension({
      autoLink: true,
      markOverride: { excludes: undefined },
    }),
    new HeadingExtension({ levels: [1, 2, 3, 4] }),
    new BulletListExtension(),
    new OrderedListExtension(),
    new MarkdownExtension(),
    new HistoryExtension(),
    new DropCursorExtension(),
    new NodeFormattingExtension(),
    new ReactComponentExtension(),
    new ImageExtension({
      uploadHandler: imageUploader
        ? (files) => {
            const filePromises = files.map((fileWithProgress) =>
              imageUploader(fileWithProgress.file, fileWithProgress.progress),
            );
            return filePromises.map((promise) => () => promise);
          }
        : undefined,
      enableResizing: false,
    }),
    new MentionAtomExtension({
      matchers: [{ name: 'at', char: '@', matchOffset: 0 }],
    }),
    new FileExtension({ uploadFileHandler: fileUploader }),
    new EventsExtension(),
    new TodoExtension(),
    new MouseTrackerExtension({ isDisabled, isMobile, hasBorder }),
    new TrailingNodeExtension({ ignoredNodes: [], nodeName: 'paragraph' }),
    new HorizontalRuleExtension(),
    new TextHighlightExtension(),
    new LinkCardExtension(),
  ];
}

export interface TextEditorProps {
  // onChange?: (json: { [key: string]: any }) => void;
  onChange?: (content: string) => void;
  placeholder?: string;
  data?: { [key: string]: any };
  pageId?: string;
  attachments?: boolean;

  editable?: boolean;
  spaceId?: string;
  minHeight?: string;
  showTemplates?: boolean;
  disableDnd?: boolean;
  hasBorder?: boolean;
  autoFocus?: boolean;
  hidePlusButton?: boolean;
  showFixedToolbar?: boolean;
}

interface TextEditorAuxPropx extends TextEditorProps {
  fileUploader: () => FileUploader<FileAttributes>;
  imageUploader: ImageUploaderFunction;
}

const TextEditor = forwardRef<TextEditorAuxPropx, 'div'>(
  (
    {
      onChange,
      placeholder,
      data,
      pageId,
      attachments = true,
      editable = true,
      spaceId,
      minHeight,
      fileUploader,
      imageUploader,
      showTemplates,
      disableDnd,
      hasBorder = false,
      autoFocus = false,
      hidePlusButton = false,
      showFixedToolbar = false,
    },
    ref,
  ) => {
    const textColor = useColorModeValue('black', 'white');
    const activeLayout = useActiveLayout();
    const isMobile = activeLayout !== Layout.DESKTOP;

    const content =
      data && (data as RemirrorJSON).type
        ? changeFilesUrlToCurrentLocation(data)
        : undefined;

    const [currentContent, setCurrentContent] = useState(
      JSON.stringify(content || ''),
    );

    const onError: InvalidContentHandler = useCallback(
      ({ json, invalidContent, transformers, error }) => {
        Sentry.withScope((s) => {
          s.setLevel('warning');
          s.setTag('editor', 'TextEditor');
          Sentry.captureException(error);
        });
        // Automatically remove all invalid nodes and marks.
        return transformers.remove(json, invalidContent);
      },
      [],
    );
    const { manager, state } = useRemirror({
      extensions: getExtensions(
        fileUploader,
        imageUploader,
        isMobile || !!disableDnd,
        isMobile,
        hasBorder,
      ),
      content,
      onError,
    });

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          manager.view.focus();
        },
      }),
      [manager.view],
    );

    const {
      isOpen: isOpenLinkTool,
      onClose: onCloseLinkTool,
      onOpen: onOpenLinkTool,
    } = useDisclosure();

    return (
      <Box
        flex={1}
        width="full"
        maxW="75ch"
        bg="var(--editor-bg-color, transparent)"
        color={`var(--editor-color, ${textColor})`}
        data-qa="document-editor"
        userSelect="auto"
        paddingLeft={!isMobile && !hidePlusButton ? '40px' : undefined}
      >
        <AllStyledComponent style={{ flex: 1 }}>
          <ThemeProvider
            theme={{
              space: { 3: '10px 10px 50px', 6: minHeight },
              color: { text: textColor },
              radius: { border: 1 },
            }}
          >
            <Remirror
              manager={manager}
              initialContent={state}
              autoFocus={autoFocus}
              placeholder={placeholder ?? t`Type away or choose a template`}
              onChange={(e) => {
                const newContent = JSON.stringify(e.state.doc.toJSON());
                if (newContent !== currentContent) {
                  setCurrentContent(newContent);
                  onChange?.(newContent);
                }
              }}
              editable={editable}
            >
              {editable && !hidePlusButton && (
                <AddBlocksToolbar pageId={pageId} />
              )}
              {showFixedToolbar && (
                <CollapsingToolbar
                  editable={editable}
                  show
                  onOpenLinkTool={onOpenLinkTool}
                  withHighlightTool
                  isFixedToolbar
                />
              )}
              {editable && spaceId && <UserSuggester spaceId={spaceId} />}
              <Box
                overflow="hidden"
                flex={1}
                onClick={(e) => {
                  if (!editable && e.target instanceof HTMLAnchorElement) {
                    window.__handleLinkClick &&
                      window.__handleLinkClick(e as unknown as MouseEvent);
                  }
                }}
              >
                <EditorComponent />
                {showTemplates && <TemplateExtension />}
              </Box>
              {!showFixedToolbar && (
                <FloatingToolbar
                  editable={editable}
                  onOpenLinkTool={onOpenLinkTool}
                />
              )}
              <LinkToolModal
                isOpen={isOpenLinkTool}
                onClose={onCloseLinkTool}
              />
              {editable && <DetectLinkCardDialog />}
            </Remirror>
          </ThemeProvider>
        </AllStyledComponent>
      </Box>
    );
  },
);

export const TextEditorAux = forwardRef<TextEditorProps, 'div'>(
  (props, ref) => {
    const [editorKey, setEditorKey] = useState(1);
    const [lastData, setLastData] = useState(''); // Choosing random default here
    const fileUploader = useUploadFileHandler({ pageId: props.pageId });
    const imageUploader = useUploadImageHandler({ pageId: props.pageId });

    // very dirty hack to detect and force changes in Editor
    useEffect(() => {
      if (!props.editable) {
        const dataString = JSON.stringify(props.data);
        if (dataString !== lastData) {
          setLastData(dataString);
          setEditorKey((i) => i + 1);
        }
      }
    }, [props.editable, props.data, setEditorKey, lastData]);

    return (
      <ErrorBoundary fallback={<TextEditorError />}>
        <TextEditor
          {...props}
          ref={ref}
          key={editorKey}
          fileUploader={fileUploader}
          imageUploader={imageUploader}
        />
      </ErrorBoundary>
    );
  },
);

export default TextEditorAux;

const TextEditorError = () => {
  const textColor = useColorModeValue('black', 'white');
  return (
    <Box
      flex={1}
      width="full"
      maxW="75ch"
      bg="var(--editor-bg-color, transparent)"
      color={`var(--editor-color, ${textColor})`}
      data-qa="document-editor"
      userSelect="auto"
    >
      Text editor crashed.
    </Box>
  );
};
